import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

export const useTokenRefresh = () => {
  const refreshToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;

      // Verificar se o token está próximo do vencimento
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      const timeUntilExpiry = tokenData.exp - now;

      // Se o token expira em menos de 5 minutos, tentar renovar
      if (timeUntilExpiry < 300) {
        const response = await axios.post('/api/v1/auth/refresh', {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          return response.data.token;
        }
      }

      return token;
    } catch (error) {
      console.error('Erro ao renovar token:', error);

      if (error.response?.status === 401) {
        toast.error("Sessão expirada", {
          description: "Faça login novamente para continuar",
          duration: 4000,
        });

        // Limpar dados de autenticação
        localStorage.removeItem('token');
        localStorage.removeItem('fullname');
        localStorage.removeItem('role');
        localStorage.removeItem('email');

        return null;
      }

      return null;
    }
  }, []);

  const makeAuthenticatedRequest = useCallback(async (requestFunction) => {
    try {
      const token = await refreshToken();
      if (!token) {
        throw new Error('Token inválido');
      }

      // Se a função de requisição não recebe o token como parâmetro,
      // precisamos modificá-la para incluir o token nos headers
      if (typeof requestFunction === 'function') {
        // Verificar se a função já espera o token como parâmetro
        const result = await requestFunction();
        
        // Se a requisição falhou por falta de token, tentar novamente com token
        if (result && result.config && !result.config.headers?.Authorization) {
          result.config.headers = {
            ...result.config.headers,
            Authorization: `Bearer ${token}`
          };
          return await axios(result.config);
        }
        
        return result;
      }

      return await requestFunction(token);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Sessão expirada", {
          description: "Faça login novamente para continuar",
          duration: 4000,
        });
      }
      throw error;
    }
  }, [refreshToken]);

  return {
    refreshToken,
    makeAuthenticatedRequest
  };
};
